import React, { useState, useEffect, useRef } from 'react';
import { X, Send, User, Loader2, Sparkles, Database, Globe } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useAuth } from '../context/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { supabase } from '../lib/supabase';
import { GoogleGenerativeAI, SchemaType, FunctionDeclarationsTool } from '@google/generative-ai';

interface ChatMessage {
  id: string;
  role: 'user' | 'model';
  text: string;
}

export const AIChatWidget = () => {
  const isMobile = useIsMobile(768);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [mode, setMode] = useState<'erp' | 'allgemein'>('erp');
  
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // App Context for RAG & CRUD
  const { customers, inventory, orders, employees, machines, vehicles, addOrder, updateInventoryQuantity } = useData() as any;
  const { currentTenant } = useAuth() as any;

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    setMessages(prev => [...prev, { id: Date.now().toString(), role: 'user', text: userMessage }]);
    setIsLoading(true);

    try {
      // Secure Key Retrieval with fallback
      const apiKey = currentTenant?.geminiApiKey || import.meta.env.VITE_GEMINI_API_KEY;
      const modelName = 'gemini-3.1-flash-lite';

      if (!apiKey) {
        setMessages(prev => [...prev, { 
          id: Date.now().toString(), 
          role: 'model', 
          text: 'Fehler: Die KI ist aktuell nicht konfiguriert (Fehlender Platform API-Key).' 
        }]);
        setIsLoading(false);
        return;
      }

      // 1. RAG Flow - Text-embedding-004 + Supabase match_kb_documents RPC
      let kbContext = '';
      try {
        const genAI = new GoogleGenerativeAI(apiKey);
        const embeddingModel = genAI.getGenerativeModel({ model: 'gemini-embedding-001' });
        const embeddingResult = await embeddingModel.embedContent(userMessage);
        const embedding = embeddingResult.embedding.values;

        const { data: documents, error } = await supabase.rpc('match_kb_documents', {
          query_embedding: embedding,
          match_threshold: 0.3,
          match_count: 5,
          tenant_id_filter: currentTenant?.id || ''
        });

        if (error) {
          console.error("RAG RPC Search Error:", error);
        } else if (documents && documents.length > 0) {
          kbContext = "\nRELEVANTE EINTRAEGE AUS DER FIRMEN-WISSENSDATENBANK:\n" + 
            documents.map((doc: any) => `[Titel: ${doc.title}]: ${doc.content}`).join("\n") + "\n";
        }
      } catch (ragErr) {
        console.error("RAG Knowledge Base query failed:", ragErr);
      }

      const genAI = new GoogleGenerativeAI(apiKey);
      
      let systemContext: string;

      if (mode === 'erp') {
        const inventoryCtx = inventory.slice(0, 50).map((i:any) => `${i.name} (ID: ${i.id}): ${i.quantity}${i.unit||''} à ${i.price||'?'}€`).join(', ');
        const customersCtx = customers.slice(0, 30).map((c:any) => `${c.name} (ID: ${c.id}, Adresse: ${c.address||''})`).join(' | ');
        const ordersCtx = orders.filter((o:any) => o.status !== 'Abgeschlossen' && o.status !== 'Storniert').slice(0, 20).map((o:any) => `${o.title} (ID: ${o.id}) [Status: ${o.status}] am ${o.scheduledStart||''}`).join(' | ');
        const employeesCtx = employees.slice(0, 20).map((e:any) => `${e.name} (ID: ${e.id})`).join(', ');
        const machinesCtx = machines.slice(0, 10).map((m:any) => `${m.name} [ID: ${m.id}, Status: ${m.status||''}]`).join(', ');
        const vehiclesCtx = vehicles.slice(0, 10).map((v:any) => `${v.name} [ID: ${v.id}, Kennzeichen: ${v.licensePlate||''}]`).join(', ');

        const tenantName = currentTenant?.name || 'Betriebszentrale';
        systemContext = `Du bist die KI-Assistenz des ERP-Systems "${tenantName}". Antworte auf Deutsch, präzise, professionell und hilfreich. Du kennst alle internen Daten der Firma.

${kbContext}

LIVE-DATEN DES UNTERNEHMENS:
Lager: ${inventoryCtx}
Kunden: ${customersCtx}
Aktive Aufträge: ${ordersCtx}
Mitarbeiter: ${employeesCtx}
Maschinen: ${machinesCtx}
Fahrzeuge: ${vehiclesCtx}

Du hast Zugriff auf Werkzeuge (Tools) zur Interaktion mit dem ERP-System:
1. createOrder: Nutze dies, um neue Aufträge direkt im System anzulegen.
2. checkInventory: Nutze dies, um den Lagerbestand spezifisch abzufragen.
3. updateStock: Nutze dies, um Bestandsmengen anzupassen (Wareneingang / Materialverbrauch).

WICHTIGE VERHALTENSREGELN FÜR AKTIONEN / WERKZEUGE (Action-AI):
- KEINE ID-HALLUZINATIONEN: Erfinde NIEMALS UUIDs, IDs oder Artikelnummern. Verwende ausschließlich IDs, die exakt in den obigen LIVE-DATEN aufgeführt sind.
- INTERAKTIVES NACHFRAGEN BEI FEHLENDEN INFORMATIONEN: Wenn der Nutzer eine Aktion wünscht (z. B. "Erstelle einen neuen Auftrag"), aber wichtige Daten wie der Kundename, das Datum oder der Titel des Auftrags fehlen, darfst du das Werkzeug "createOrder" NICHT aufrufen. Frage den Nutzer stattdessen freundlich und präzise nach den fehlenden Daten.
- NAMENS-ZUORDNUNG: Wenn der Nutzer einen Kundennamen (z. B. "Müller") oder Artikelnamen (z. B. "Kupferrohr") nennt, suche in den LIVE-DATEN nach passenden Einträgen:
  * Falls ein Eintrag eindeutig existiert, nutze dessen ID (UUID) für den Tool-Aufruf.
  * Falls kein Eintrag oder mehrere Einträge existieren (z. B. "Müller GmbH" und "Klaus Müller"), rufe das Tool NICHT auf, sondern frage den Nutzer nach Klarstellung (z. B. "Welchen Kunden Müller meinst du? Ich habe Müller GmbH und Klaus Müller gefunden.").
- SCHRITT-FÜR-SCHRITT-FÜHRUNG: Führe den Nutzer bei komplexen Anfragen (z. B. Auftragserstellung) an der Hand. Sag ihm genau, welche Parameter du noch benötigst, um die Aktion erfolgreich abzuschließen.

Beantworte Fragen auf Basis dieser Firmendaten und der RAG-Wissensdatenbank. Wenn du Aktionen ausführst, erkläre dem Nutzer im Anschluss, was du getan hast.`;
      } else {
        const tenantName = currentTenant?.name || 'Betriebszentrale';
        systemContext = `Du bist die KI-Assistenz des ERP-Systems "${tenantName}". Im Allgemein-Modus beantwortest du allgemeine Fragen aus deinem Wissen (z.B. Fahrtzeiten, Preise, technische Fragen, Gesetze, Tipps für Handwerker). Antworte auf Deutsch, präzise und hilfreich.

${kbContext}`;
      }

      // Declarations of Action-AI tools
      const tools: FunctionDeclarationsTool[] = [{
        functionDeclarations: [
          {
            name: 'createOrder',
            description: 'Erstellt einen neuen Auftrag im ERP-System.',
            parameters: {
              type: SchemaType.OBJECT,
              properties: {
                title: { type: SchemaType.STRING, description: 'Titel oder Kurzbeschreibung des Auftrags.' },
                customerId: { type: SchemaType.STRING, description: 'Die UUID des Kunden.' },
                description: { type: SchemaType.STRING, description: 'Beschreibung der durchzuführenden Arbeiten.' },
                scheduledDate: { type: SchemaType.STRING, description: 'Geplantes Datum im Format YYYY-MM-DD.' }
              },
              required: ['title', 'customerId', 'scheduledDate']
            }
          },
          {
            name: 'checkInventory',
            description: 'Fragt den aktuellen Bestand eines bestimmten Artikels im Lager ab.',
            parameters: {
              type: SchemaType.OBJECT,
              properties: {
                itemName: { type: SchemaType.STRING, description: 'Der Name des Artikels (z.B. Zement, Kupferrohr).' }
              },
              required: ['itemName']
            }
          },
          {
            name: 'updateStock',
            description: 'Aktualisiert den Lagerbestand eines Artikels.',
            parameters: {
              type: SchemaType.OBJECT,
              properties: {
                itemId: { type: SchemaType.STRING, description: 'Die UUID des Artikels.' },
                quantityChange: { type: SchemaType.NUMBER, description: 'Die Differenz (positiv für Zuwachs/Lieferung, negativ für Verbrauch).' }
              },
              required: ['itemId', 'quantityChange']
            }
          }
        ]
      }];

      const model = genAI.getGenerativeModel({ 
        model: modelName,
        systemInstruction: systemContext,
        tools: mode === 'erp' ? tools : undefined
      });

      const chat = model.startChat({
        history: messages.map(m => ({
          role: m.role,
          parts: [{ text: m.text }]
        }))
      });

      let responseText = '';
      let result = await chat.sendMessage(userMessage);
      let functionCalls = result.response.functionCalls;
      let loopCount = 0;

      while (functionCalls && functionCalls.length > 0 && loopCount < 5) {
        loopCount++;
        const functionCall = functionCalls[0];
        const { name, args } = functionCall;
        
        let toolResponseText = '';
        let actionNotice = '';

        if (name === 'checkInventory') {
          const { itemName } = args as any;
          if (!itemName || itemName.trim() === '') {
            toolResponseText = `Fehler: Parameter "itemName" fehlt. Bitte frage den Nutzer nach dem Namen des gesuchten Artikels.`;
          } else {
            actionNotice = `🔍 KI prüft den Lagerbestand für "${itemName}"...`;
            setMessages(prev => [...prev, { id: `act-${Date.now()}-${loopCount}`, role: 'model', text: actionNotice }]);

            const items = inventory.filter((i: any) => i.name.toLowerCase().includes(itemName.toLowerCase()));
            if (items.length === 0) {
              toolResponseText = `Keine Artikel im Lager gefunden, die "${itemName}" entsprechen.`;
            } else {
              toolResponseText = items.map((i: any) => `Artikel: ${i.name} (ID: ${i.id}), Bestand: ${i.quantity} ${i.unit || 'Stück'}, Preis: ${i.price || '0'}€`).join('\n');
            }
          }
        } else if (name === 'updateStock') {
          const { itemId, quantityChange } = args as any;
          
          if (!itemId) {
            toolResponseText = `Fehler: Parameter "itemId" fehlt. Bitte frage den Nutzer nach dem Namen oder der ID des Artikels.`;
          } else if (quantityChange === undefined || isNaN(Number(quantityChange))) {
            toolResponseText = `Fehler: Parameter "quantityChange" ist ungültig oder fehlt. Bitte frage den Nutzer nach der Menge, die gebucht werden soll.`;
          } else {
            let item = inventory.find((i: any) => i.id === itemId);
            if (!item && itemId) {
              const matchingItems = inventory.filter((i: any) => 
                i.name.toLowerCase().includes(itemId.toLowerCase()) ||
                itemId.toLowerCase().includes(i.name.toLowerCase())
              );
              if (matchingItems.length === 1) {
                item = matchingItems[0];
              } else if (matchingItems.length > 1) {
                toolResponseText = `Fehler: Der Artikelname "${itemId}" ist mehrdeutig. Gefundene Optionen:\n` + 
                  matchingItems.map((i: any) => `- "${i.name}"`).join('\n') + 
                  `\nBitte frage den Nutzer nach einer genauen Auswahl.`;
              }
            }

            if (!item) {
              if (!toolResponseText) {
                toolResponseText = `Fehler: Der Artikel "${itemId}" wurde nicht im Lager gefunden. Verfügbare Artikel im Lager:\n` +
                  inventory.map((i: any) => `- "${i.name}"`).join('\n') +
                  `\nBitte frage den Nutzer nach dem genauen Namen.`;
              }
            } else {
              actionNotice = `📦 KI aktualisiert Lagerbestand für "${item.name}" um ${quantityChange > 0 ? '+' : ''}${quantityChange}...`;
              setMessages(prev => [...prev, { id: `act-${Date.now()}-${loopCount}`, role: 'model', text: actionNotice }]);

              try {
                await updateInventoryQuantity(item.id, Number(quantityChange));
                toolResponseText = `Erfolgreich aktualisiert: Der Lagerbestand von "${item.name}" wurde um ${quantityChange} geändert. Neuer Bestand: ${(item.quantity || 0) + Number(quantityChange)} ${item.unit || 'Stück'}.`;
              } catch (dbErr: any) {
                console.error("DB Stock Update Error:", dbErr);
                toolResponseText = `Fehler beim Aktualisieren des Lagerbestands in der Datenbank: ${dbErr.message || dbErr}. Bitte versuche es erneut.`;
              }
            }
          }
        } else if (name === 'createOrder') {
          const { title, customerId, description, scheduledDate } = args as any;

          if (!title || title.trim() === '') {
            toolResponseText = `Fehler: Parameter "title" (Titel des Auftrags) fehlt oder ist leer. Bitte frage den Nutzer nach dem Titel des Auftrags.`;
          } else if (!customerId) {
            toolResponseText = `Fehler: Parameter "customerId" (Kunde) fehlt oder ist leer. Bitte frage den Nutzer nach dem Kundennamen.`;
          } else if (!scheduledDate || scheduledDate.trim() === '') {
            toolResponseText = `Fehler: Parameter "scheduledDate" (geplantes Datum) fehlt oder ist leer. Bitte frage den Nutzer nach dem gewünschten Datum im Format YYYY-MM-DD.`;
          } else {
            let customer = customers.find((c: any) => c.id === customerId);
            
            if (!customer && customerId) {
              const matchingCustomers = customers.filter((c: any) => 
                c.name.toLowerCase().includes(customerId.toLowerCase()) ||
                customerId.toLowerCase().includes(c.name.toLowerCase())
              );
              if (matchingCustomers.length === 1) {
                customer = matchingCustomers[0];
              } else if (matchingCustomers.length > 1) {
                toolResponseText = `Fehler: Der Kundenname "${customerId}" ist mehrdeutig. Gefundene Optionen:\n` +
                  matchingCustomers.map((c: any) => `- "${c.name}"`).join('\n') +
                  `\nBitte frage den Nutzer nach einer genauen Auswahl.`;
              }
            }

            if (!customer) {
              if (!toolResponseText) {
                toolResponseText = `Fehler: Der Kunde "${customerId}" wurde nicht im System gefunden. Verfügbare Kunden:\n` +
                  customers.map((c: any) => `- "${c.name}"`).join('\n') +
                  `\nBitte frage den Nutzer nach dem genauen Kundennamen.`;
              }
            } else {
              const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
              if (!dateRegex.test(scheduledDate)) {
                toolResponseText = `Fehler: Das Datum "${scheduledDate}" hat kein gültiges Format (YYYY-MM-DD). Bitte frage den Nutzer nach dem richtigen Datum.`;
              } else {
                actionNotice = `📅 KI erstellt neuen Auftrag "${title}" für "${customer.name}"...`;
                setMessages(prev => [...prev, { id: `act-${Date.now()}-${loopCount}`, role: 'model', text: actionNotice }]);

                const customerAddress = customer.address || '';
                const lat = customer.lat || undefined;
                const lng = customer.lng || undefined;

                const newOrder = {
                  id: crypto.randomUUID(),
                  customerId: customer.id,
                  title,
                  status: 'Bereit',
                  scheduledStart: scheduledDate,
                  scheduledEnd: scheduledDate,
                  serviceAddress: customerAddress,
                  notes: description || '',
                  lat,
                  lng,
                  assignedEmployees: [],
                  assignedVehicles: [],
                  assignedMachines: [],
                  requiredMaterials: [],
                  actualHours: 0,
                  hourlyRate: 75.0,
                  travelCosts: 0.0,
                  revenue: 0.0
                };
                
                try {
                  await addOrder(newOrder);
                  toolResponseText = `Erfolgreich erstellt: Der Auftrag "${title}" für den Kunden "${customer.name}" wurde für den ${scheduledDate} angelegt.`;
                } catch (dbErr: any) {
                  console.error("DB Order Insert Error:", dbErr);
                  toolResponseText = `Fehler beim Erstellen des Auftrags in der Datenbank: ${dbErr.message || dbErr}. Bitte versuche es erneut.`;
                }
              }
            }
          }
        }

        result = await chat.sendMessage([{
          functionResponse: {
            name,
            response: { result: toolResponseText }
          }
        }]);
        functionCalls = result.response.functionCalls;
      }

      responseText = result.response.text();
      setMessages(prev => [...prev, { id: Date.now().toString(), role: 'model', text: responseText }]);
    } catch (error: any) {
      console.error("AI Chat Error:", error);
      setMessages(prev => [...prev, { 
        id: Date.now().toString(), 
        role: 'model', 
        text: `Es gab einen Fehler bei der KI-Anfrage: ${error.message}` 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {/* Floating Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="btn"
        style={{
          position: 'fixed',
          bottom: isMobile ? 'calc(68px + var(--sab))' : '24px',
          right: isMobile ? '16px' : '24px',
          width: '52px',
          height: '52px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, var(--ms-blue-primary) 0%, #005a9e 100%)',
          color: '#ffffff',
          boxShadow: 'var(--sys-elevation-3)',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 9998,
          transition: 'transform var(--transition-fast), box-shadow var(--transition-fast)'
        }}
        title="Betriebszentrale Copilot KI"
        aria-label="KI Copilot öffnen"
      >
        <Sparkles size={24} />
      </button>

      {/* Chat Window / Mobile Bottom Sheet */}
      <div 
        style={{
          position: 'fixed',
          bottom: isMobile ? 0 : '24px',
          right: isMobile ? 0 : '24px',
          left: isMobile ? 0 : 'auto',
          width: isMobile ? '100vw' : '420px',
          maxWidth: isMobile ? '100vw' : 'calc(100vw - 40px)',
          height: isMobile ? '90vh' : '620px',
          maxHeight: isMobile ? '90vh' : 'calc(100vh - 40px)',
          backgroundColor: 'var(--ms-bg-surface)',
          borderRadius: isMobile ? '20px 20px 0 0' : 'var(--sys-shape-corner-medium)',
          border: isMobile ? 'none' : '1px solid var(--ms-border)',
          borderTop: isMobile ? '1px solid var(--ms-border)' : '1px solid var(--ms-border)',
          boxShadow: 'var(--sys-elevation-4)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 10000,
          overflow: 'hidden',
          transition: 'transform var(--transition-smooth), opacity var(--transition-smooth)',
          transform: isOpen ? 'translateY(0) scale(1)' : isMobile ? 'translateY(100%)' : 'translateY(24px) scale(0.94)',
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          paddingBottom: isMobile ? 'var(--sab)' : 0
        }}
      >
        {/* Header */}
        <div style={{ 
          padding: '12px 16px', 
          background: 'linear-gradient(135deg, var(--ms-blue-primary) 0%, #005a9e 100%)', 
          color: '#ffffff', 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          boxShadow: 'var(--sys-elevation-1)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{
              width: '28px',
              height: '28px',
              borderRadius: '6px',
              background: 'rgba(255, 255, 255, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '1px solid rgba(255, 255, 255, 0.25)'
            }}>
              <Sparkles size={16} color="#ffffff" />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.92rem', fontWeight: 700, color: '#ffffff', letterSpacing: '0.2px' }}>Betriebszentrale Copilot</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.72rem', opacity: 0.9 }}>
                <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#4ade80' }} />
                <span>KI Aktiv · Gemini Pro</span>
              </div>
            </div>
          </div>
          <button 
            onClick={() => setIsOpen(false)} 
            style={{ background: 'rgba(255,255,255,0.15)', border: 'none', color: '#ffffff', cursor: 'pointer', borderRadius: '4px', padding: '4px', display: 'flex', alignItems: 'center' }}
            title="Schließen"
          >
            <X size={18} />
          </button>
        </div>

        {/* Segmented Mode Selector */}
        <div style={{ 
          display: 'flex', 
          backgroundColor: 'var(--ms-bg-surface-secondary)',
          borderBottom: '1px solid var(--ms-border)',
          padding: '6px 8px',
          gap: '6px'
        }}>
          <button
            onClick={() => { setMode('erp'); }}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: 'var(--sys-shape-corner-small)',
              border: '1px solid',
              borderColor: mode === 'erp' ? 'var(--ms-blue-primary)' : 'transparent',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all var(--transition-fast)',
              backgroundColor: mode === 'erp' ? 'var(--ms-bg-surface)' : 'transparent',
              color: mode === 'erp' ? 'var(--ms-blue-primary)' : 'var(--ms-text-secondary)',
              boxShadow: mode === 'erp' ? 'var(--sys-elevation-1)' : 'none'
            }}
          >
            <Database size={14} />
            Firmen-Daten
          </button>
          <button
            onClick={() => { setMode('allgemein'); }}
            style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '6px',
              padding: '6px 10px',
              borderRadius: 'var(--sys-shape-corner-small)',
              border: '1px solid',
              borderColor: mode === 'allgemein' ? 'var(--ms-blue-primary)' : 'transparent',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
              transition: 'all var(--transition-fast)',
              backgroundColor: mode === 'allgemein' ? 'var(--ms-bg-surface)' : 'transparent',
              color: mode === 'allgemein' ? 'var(--ms-blue-primary)' : 'var(--ms-text-secondary)',
              boxShadow: mode === 'allgemein' ? 'var(--sys-elevation-1)' : 'none'
            }}
          >
            <Globe size={14} />
            Allgemeinwissen
          </button>
        </div>

        {/* Messages Stream */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '16px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '14px', 
          backgroundColor: 'var(--ms-bg-canvas)' 
        }}>
          {messages.length === 0 && (
            <div style={{ textAlign: 'center', color: 'var(--ms-text-secondary)', margin: 'auto 0', padding: '16px' }}>
              <div style={{
                width: '54px',
                height: '54px',
                borderRadius: '50%',
                background: 'var(--ms-blue-light)',
                color: 'var(--ms-blue-primary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 12px auto'
              }}>
                <Sparkles size={28} />
              </div>
              <p style={{ fontWeight: 700, fontSize: '0.98rem', color: 'var(--ms-text-primary)' }}>Wie kann ich Ihnen helfen?</p>
              {mode === 'erp' ? (
                <p style={{ fontSize: '0.82rem', marginTop: '6px', lineHeight: 1.5, color: 'var(--ms-text-secondary)' }}>
                  Ich habe direkten Zugriff auf Aufträge, Kunden, Lagerbestände und Mitarbeiter.
                </p>
              ) : (
                <p style={{ fontSize: '0.82rem', marginTop: '6px', lineHeight: 1.5, color: 'var(--ms-text-secondary)' }}>
                  Ich beantworte Fachfragen, Normen, Berechnungen und allgemeine Handwerksthemen.
                </p>
              )}

              {/* Quick Prompt Chips */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '16px' }}>
                {mode === 'erp' ? (
                  <>
                    <button 
                      onClick={() => setInput('Welche Aufträge sind aktuell in Ausführung?')}
                      className="btn subtle" 
                      style={{ fontSize: '0.78rem', padding: '6px 10px', background: 'var(--ms-bg-surface)', border: '1px solid var(--ms-border)', borderRadius: '4px', textAlign: 'left', color: 'var(--ms-text-primary)' }}
                    >
                      📋 Welche Aufträge sind aktuell in Ausführung?
                    </button>
                    <button 
                      onClick={() => setInput('Welche Artikel im Lager haben einen kritischen Bestand?')}
                      className="btn subtle" 
                      style={{ fontSize: '0.78rem', padding: '6px 10px', background: 'var(--ms-bg-surface)', border: '1px solid var(--ms-border)', borderRadius: '4px', textAlign: 'left', color: 'var(--ms-text-primary)' }}
                    >
                      📦 Welche Lagerartikel sind knapp?
                    </button>
                  </>
                ) : (
                  <>
                    <button 
                      onClick={() => setInput('Wie berechne ich die U-Werte einer Außenwand?')}
                      className="btn subtle" 
                      style={{ fontSize: '0.78rem', padding: '6px 10px', background: 'var(--ms-bg-surface)', border: '1px solid var(--ms-border)', borderRadius: '4px', textAlign: 'left', color: 'var(--ms-text-primary)' }}
                    >
                      💡 Wie berechne ich den U-Wert einer Wand?
                    </button>
                    <button 
                      onClick={() => setInput('Formuliere ein freundliches Anschreiben für ein Angebot')}
                      className="btn subtle" 
                      style={{ fontSize: '0.78rem', padding: '6px 10px', background: 'var(--ms-bg-surface)', border: '1px solid var(--ms-border)', borderRadius: '4px', textAlign: 'left', color: 'var(--ms-text-primary)' }}
                    >
                      ✍️ Anschreiben für ein Angebot formulieren
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

          {messages.map((msg) => (
            <div key={msg.id} style={{ display: 'flex', flexDirection: 'column', alignItems: msg.role === 'user' ? 'flex-end' : 'flex-start' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', maxWidth: '88%', flexDirection: msg.role === 'user' ? 'row-reverse' : 'row' }}>
                <div style={{ 
                  width: '26px', height: '26px', borderRadius: '50%', display: 'flex', justifyContent: 'center', alignItems: 'center', flexShrink: 0, marginTop: '2px',
                  backgroundColor: msg.role === 'user' ? 'var(--ms-blue-primary)' : 'var(--ms-bg-surface)',
                  color: msg.role === 'user' ? '#ffffff' : 'var(--ms-blue-primary)',
                  border: msg.role === 'model' ? '1px solid var(--ms-border)' : 'none',
                  boxShadow: 'var(--sys-elevation-1)'
                }}>
                  {msg.role === 'user' ? <User size={14} /> : <Sparkles size={14} />}
                </div>
                <div style={{
                  padding: '10px 14px',
                  borderRadius: '12px',
                  borderBottomRightRadius: msg.role === 'user' ? '2px' : '12px',
                  borderBottomLeftRadius: msg.role === 'model' ? '2px' : '12px',
                  backgroundColor: msg.role === 'user' ? 'var(--ms-blue-primary)' : 'var(--ms-bg-surface)',
                  color: msg.role === 'user' ? '#ffffff' : 'var(--ms-text-primary)',
                  border: msg.role === 'model' ? '1px solid var(--ms-border)' : 'none',
                  boxShadow: 'var(--sys-elevation-1)',
                  fontSize: '0.865rem',
                  lineHeight: '1.45',
                  whiteSpace: 'pre-wrap'
                }}>
                  {msg.text}
                </div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <div style={{ width: '26px', height: '26px', borderRadius: '50%', backgroundColor: 'var(--ms-bg-surface)', color: 'var(--ms-blue-primary)', border: '1px solid var(--ms-border)', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Sparkles size={14} />
              </div>
              <div style={{ padding: '10px 14px', borderRadius: '12px', borderBottomLeftRadius: '2px', backgroundColor: 'var(--ms-bg-surface)', border: '1px solid var(--ms-border)', color: 'var(--ms-text-secondary)', display: 'flex', gap: '6px', alignItems: 'center' }}>
                <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} />
                <span style={{ fontSize: '0.8rem', fontWeight: 500 }}>Copilot denkt nach...</span>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{ padding: '12px 14px', backgroundColor: 'var(--ms-bg-surface)', borderTop: '1px solid var(--ms-border)' }}>
          <div style={{ 
            display: 'flex', 
            gap: '6px', 
            alignItems: 'center', 
            backgroundColor: 'var(--ms-bg-surface-secondary)', 
            border: '1px solid var(--ms-border-input)',
            padding: '3px 4px 3px 12px', 
            borderRadius: 'var(--sys-shape-corner-full)',
            transition: 'border-color var(--transition-fast)'
          }}>
            <input 
              type="text" 
              placeholder={mode === 'erp' ? 'Frage zu Aufträgen, Kunden, Lager...' : 'Stelle eine allgemeine Frage...'}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              style={{ flex: 1, border: 'none', background: 'transparent', outline: 'none', fontSize: '0.85rem', color: 'var(--ms-text-primary)' }}
            />
            <button 
              onClick={handleSend}
              disabled={isLoading || !input.trim()}
              style={{ 
                width: '32px', height: '32px', borderRadius: '50%', border: 'none', 
                backgroundColor: input.trim() && !isLoading ? 'var(--ms-blue-primary)' : 'transparent',
                color: input.trim() && !isLoading ? '#ffffff' : 'var(--ms-text-disabled)',
                display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: input.trim() && !isLoading ? 'pointer' : 'default',
                transition: 'all var(--transition-fast)'
              }}
              aria-label="Nachricht senden"
            >
              <Send size={15} style={{ marginLeft: '1px' }} />
            </button>
          </div>
          <div style={{ textAlign: 'center', marginTop: '6px', fontSize: '0.68rem', color: 'var(--ms-text-muted)' }}>
            {mode === 'erp' ? '📦 Betriebs-Daten Modus' : '🌍 Allgemeinwissen Modus'} · Antworten können variieren
          </div>
        </div>
      </div>
    </>
  );
};
